import { cva } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { ButtonHTMLAttributes, MouseEventHandler, PropsWithChildren } from 'react';
import { cn } from '../../util';
import { Icon } from '../Icon';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'accent' | 'transparent' | 'danger';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
  size?: 'sm' | 'm' | 'l';
  'data-testid'?: string;
};

type IconLayout = 'start' | 'end';

type TextButtonProps = ButtonProps &
  PropsWithChildren & {
    icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    iconLayout?: IconLayout;
    iconSize?: 'sm' | 'm' | 'l';
  };

const buttonVariants = cva(
  'cursor-pointer text-base font-bold transition-colors duration-200 border-2 disabled:pointer-events-none disabled:border-gray-500 disabled:bg-gray-500 disabled:text-gray-300',
  {
    variants: {
      variant: {
        primary:
          'text-white bg-gradient-to-br from-gray-700 to-background border-accent hover:from-accent/50 hover:to-background',
        accent: 'bg-accent text-secondary border-accent hover:brightness-110',
        danger: 'bg-danger text-white border-danger hover:brightness-110',
        transparent: 'bg-transparent border-transparent hover:bg-white/5 text-white'
      },
      size: {
        sm: 'px-2 py-1 text-sm rounded-sm',
        m: 'px-4 py-2 rounded-md',
        l: 'px-6 py-4 rounded-xl'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'l'
    }
  }
);

const ButtonContent = ({
  icon,
  iconLayout = 'start',
  iconSize = 'l',
  isLoading,
  children
}: {
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  iconLayout?: IconLayout;
  iconSize?: 'sm' | 'm' | 'l';
  isLoading?: boolean;
} & PropsWithChildren) => {
  return (
    <div
      className={cn(
        'flex h-4 items-center justify-center gap-2 whitespace-nowrap',
        iconLayout === 'end' && 'flex-row-reverse'
      )}
    >
      {isLoading && <LoaderCircle className="animate-spin" />}
      {icon && !isLoading && <Icon type={icon} size={iconSize} />}
      {children}
    </div>
  );
};

export const Button = ({
  variant = 'primary',
  size = 'l',
  isLoading,
  icon,
  iconLayout,
  iconSize,
  children,
  className,
  ...buttonProps
}: TextButtonProps) => {
  const contentProps = { icon, iconLayout, iconSize, isLoading, children };
  return (
    <button {...buttonProps} className={cn(buttonVariants({ variant, size }), className)}>
      <ButtonContent {...contentProps} />
    </button>
  );
};
